"use client";

import React, { useEffect, useMemo, useState } from "react";
import { cn, dayjs } from "utils";
import { DistributionCardProps, TAB } from "./types";

import { Asset } from "./Asset";
import { AssetType } from "./AssetType";
import { History } from "./History";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Portfolio/AssetInsight/DistributionCard/DistributionCard";

const filterTab = [
  { label: "24H", value: "1d" },
  { label: "7D", value: "7d" },
  { label: "30D", value: "30d" },
  { label: "90D", value: "90d" },
  { label: "180D", value: "180d" },
];

/**
 * @description 展示资产分布饼图及列表的基础骨架组件。
 */
export const DistributionCard: React.FC<DistributionCardProps> = ({
  className,
  onViewAll,
  history,
  setPeriod,
  period,
  topAssets,
  assetTypes,
  isFetching,
  startTime,
  hideAssetTypes = false,
  hideAsset = false,
}) => {
  const [activeTab, setActiveTab] = useState<TAB>("history");
  // const [activeFilterTab, setActiveFilterTab] = useState<string>("1d");
  // const [isLoading, setIsLoading] = useState(true);
  const locale = I18nUtil.getCurrentLocale();
  const I18n = useMemo(
    () => I18nUtil.createScoped(Translations, locale),
    [locale],
  );

  const tabs = useMemo<{ label: string; value: TAB; hidden: boolean }[]>(() => {
    return [
      {
        label: I18n.t("History"),
        value: "history" as const,
        hidden: false,
      },
      {
        label: I18n.t("Asset"),
        value: "asset" as const,
        hidden: hideAsset || !topAssets || topAssets.length === 0,
      },
      {
        label: I18n.t("Asset types"),
        value: "asset_types" as const,
        hidden: hideAssetTypes || !assetTypes || assetTypes.length === 0,
      },
    ].filter((item) => !item.hidden);
  }, [I18n, topAssets, assetTypes, hideAssetTypes, hideAsset]);
  const historyTab = useMemo(() => {
    const hours = dayjs().diff(startTime, "hour");
    if (hours > 48 || !startTime) {
      return filterTab;
    }
    return filterTab.slice(0, 2);
  }, [startTime]);

  useEffect(() => {
    if (!tabs.length) {
      return;
    }
    const tab = tabs.find((item) => item.value === activeTab);
    if (!tab) {
      setActiveTab(tabs[0].value);
    }
  }, [tabs, activeTab]);
  return (
    <article
      aria-label="Distribution Card article"
      data-openreplay-label="Distribution Card article Semantic"
      id="sem-distribution-card-article"
      className={cn(
        "flex flex-col gap-3 px-5 pb-5 pt-3 relative",
        "md:p-0",
        className,
      )}
    >
      {/* {isLoading && (
        <div className="absolute z-10 inset-x-5 flex flex-col gap-12 md:gap-3 before:content-[''] before:w-32 before:h-7 before:rounded-1 before:bg-background_white_overlay5 before:block">
          <LineChartSkeleton className="h-64 md:h-46.5 w-full" />
        </div>
      )} */}
      <header
        aria-label="Distribution Card header"
        data-openreplay-label="Distribution Card header Semantic"
        id="sem-distribution-card-header"
        className={cn(
          "flex flex-col gap-3",
          "md:flex-row md:justify-between md:items-center",
          // { "opacity-0": isLoading },
        )}
      >
        {tabs.length === 1 ? (
          <p className="h-7 body_medium_m flex items-start shrink-0 text-content_primary">
            {tabs[0].label}
          </p>
        ) : (
          <tab-indicator
            className="flex shrink-0 gap-6 no-scrollbar [--underline-color:var(--edgen-border\_brand\_solid)] [--animation-duration:0.2s]"
            effect="underline"
          >
            {tabs.map((item) => (
              <p
                data-openreplay-label={`DistributionCard · DistributionCard · p | ${item.label}`}
                role="tab-item"
                className="tab_item h-7 body_medium_m flex items-start"
                data-actived={activeTab === item.value}
                key={item.value}
                onClick={() => setActiveTab(item.value)}
              >
                {item.label}
              </p>
            ))}
          </tab-indicator>
        )}
        <tab-indicator
          className={cn(
            "flex shrink-0 rounded-1 border-px border-border_solid border-solid w-fit h-6 box-border overflow-hidden",
            "[--capsule-bg-color:#2c2d0a0d] [--animation-duration:0.2s] [--capsule-border-radius:0px]",

            { hidden: activeTab !== "history" },
          )}
          effect="capsule"
        >
          {historyTab.map((item) => (
            <p
              data-openreplay-label={`DistributionCard · DistributionCard · p | ${item.label}`}
              role="tab-item"
              className={cn(
                "tab_item w-11 h-6 body_medium_s py-1 relative",
                "before:content-[''] before:absolute before:-left-px before:w-px before:h-3 before:bg-border_solid first:before:hidden",
                { "before:hidden z-10": period === item.value },
              )}
              data-actived={period === item.value}
              key={item.value}
              onClick={() => setPeriod(item.value)}
            >
              {item.label}
            </p>
          ))}
        </tab-indicator>
      </header>
      {activeTab === "history" && (
        <History history={history} isFetching={isFetching} />
      )}
      {activeTab === "asset" && (
        <Asset
          onViewall={onViewAll}
          className={cn("flex-1")}
          topAssets={topAssets}
        />
      )}
      {activeTab === "asset_types" && (
        <AssetType className={cn("flex-1")} assetTypes={assetTypes} />
      )}
    </article>
  );
};
